import React, { useEffect, useState } from 'react';
import { Modal, message } from 'antd';
import moment from 'moment';
import { patch, del } from '../../services/ApiService';

const shortDate = (value) => moment(value).format('D MMM YYYY');
const shortDateTime = (value) => moment(value).format('D MMM YYYY, HH:mm');
const errorText = (error, fallback) => error?.response?.data?.error || fallback;

const CheckInModal = ({ record, onClose, onChanged }) => {
  const checkIns = record?.checkIns || [];
  const review = record?.review || {};
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setNote(record?.review?.note || '');
  }, [record]);

  const run = async (action, success) => {
    setBusy(true);
    try {
      await action();
      message.success(success);
      onChanged();
      onClose();
    } catch (error) {
      if (!error?.handledByInterceptor) message.error(errorText(error, 'Could not update the return'));
    } finally {
      setBusy(false);
    }
  };

  const reviewUrl = `api/returns/${record?.returnId}/review`;
  const saveNote = () => run(() => patch(reviewUrl, { note }), 'Note saved');
  const close = () => run(() => patch(reviewUrl, { closed: true }), 'Return closed');
  const reopen = () => run(() => patch(reviewUrl, { closed: false }), 'Return reopened');
  const unlink = () =>
    Modal.confirm({
      title: 'Unlink this check-in?',
      content: 'The return goes back to unmatched. Any note is kept.',
      okText: 'Unlink',
      okButtonProps: { danger: true },
      onOk: () => run(() => del(`api/returns/${record.returnId}/match`), 'Check-in unlinked'),
    });

  const manual = record?.matchSource === 'manual';
  const stale = manual && checkIns.length === 0;
  const locations = [...new Set(checkIns.map((c) => c.location).filter(Boolean))].join(', ');

  return (
    <Modal title="Return check-in" open={Boolean(record)} onCancel={onClose} footer={null} width={520}>
      {record && (
        <div className="returns-checkin">
          <dl>
            <dt>Order</dt>
            <dd>{record.orderRef || 'No order linked'}</dd>
            <dt>{manual ? 'Reference' : 'Tracking'}</dt>
            <dd>{record.matchRef || record.trackingNumber}</dd>
            {!stale && (
              <>
                <dt>Checked in</dt>
                <dd>{shortDate(checkIns[0]?.dateReturned)}</dd>
                <dt>By</dt>
                <dd>{record.checkedInBy || 'Not recorded'}</dd>
                <dt>Location</dt>
                <dd>{locations || 'Not recorded'}</dd>
              </>
            )}
          </dl>

          {stale ? (
            <p className="returns-stale">
              No check-in rows found for {record.matchRef}. The warehouse record may have been edited or removed.
            </p>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Qty</th>
                  <th>Condition</th>
                </tr>
              </thead>
              <tbody>
                {checkIns.map((checkIn, index) => (
                  <tr key={index}>
                    <td>
                      {checkIn.item}
                      {checkIn.ppid && <span className="returns-muted"> (pp{checkIn.ppid})</span>}
                    </td>
                    <td>{checkIn.quantity}</td>
                    <td>{checkIn.condition || 'Not recorded'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {record.matchSource && (
            <div className="returns-review">
              <label htmlFor="returns-review-note">Note</label>
              <textarea
                id="returns-review-note"
                maxLength={500}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                disabled={busy}
              />
              {review.noteAt && (
                <p className="returns-review-meta">
                  Last note by {review.noteBy || 'unknown'} on {shortDateTime(review.noteAt)}
                </p>
              )}
              {review.closed && (
                <p className="returns-review-meta">
                  Closed by {review.closedBy || 'unknown'} on {shortDateTime(review.closedAt)}
                </p>
              )}
              <div className="returns-review-actions">
                <button
                  type="button"
                  className="returns-track"
                  onClick={saveNote}
                  disabled={busy || note === (review.note || '')}
                >
                  Save note
                </button>
                {review.closed ? (
                  <button type="button" className="returns-track" onClick={reopen} disabled={busy}>
                    Reopen
                  </button>
                ) : (
                  <button type="button" className="returns-track" onClick={close} disabled={busy || stale}>
                    Confirm and close
                  </button>
                )}
                {manual && (
                  <button type="button" className="returns-track is-danger" onClick={unlink} disabled={busy}>
                    Unlink
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

export default CheckInModal;
